import * as Logger from "js-logger";
import Components from "slotworks/components/components";
import FooterComponent from "slotworks/components/footer/footer-component";
import canvasManager, { Orientation } from "slotworks/graphics/canvas/canvas-manager";
import GraphicsManager from "slotworks/graphics/graphics-manager";
import { MultiStyleText } from "slotworks/graphics/pixi/pixi-multistyle-text";
import LineResult from "slotworks/model/gameplay/records/results/line-result";
import SymbolWinResult from "slotworks/model/gameplay/records/results/symbol-win-result";
import WaysResult from "slotworks/model/gameplay/records/results/ways-result";
import SlotDefinition from "slotworks/model/slot-definition";
import SymbolDefinition from "slotworks/model/symbol-definition";
import Contract from "slotworks/utils/contracts/contract";
import Result from "slotworks/model/gameplay/records/results/result";

/**
 * This component will prepend the footer messages with the appropriate number
 * of winning symbols.
 *
 * In order for this to initalise correctly, it's required that a smaller symbol
 * graphic exists in the statics folder with the filename:
 * `${symbolId}_small.png`
 *
 * If no such graphics exist, this component will error out.
 */
export default class SymbolFooter extends FooterComponent {

    /**
     * Percentage of width to use as padding (0.5 default)
     */
    public static SYMBOL_PADDING: number = 0.5;

    /**
     * Pixel width of symbols (default 0, to auto size)
     */
    public static SYMBOL_SIZE: number = 0;

    /**
     * If true we will only show one symbol, with i.e "x3" prepended to the text
     */
    public static SHOW_SINGLE_SYMBOL = false;

    /**
     * multiplied by the regular size to provide the sizes used when in portrait orientation.
     */
    public static PORTRAIT_SCALE = 1;

    protected symbols: PIXI.Sprite[] = [];
    protected numberOfReels: number;
    protected footerText: MultiStyleText;
    protected symbolWidth: number;
    protected container: PIXI.Container;
    protected useLargeTextures = false;
    protected currentResult: SymbolWinResult;

    constructor() {
        super();

        if (!this.symbolsHaveSmallTextures()) {
            Logger.warn("Not all symbols have a small texture, which is recommended by this component");
            this.useLargeTextures = true;
        }

        this.numberOfReels = Components.matrix.getReels().length;
        this.footerText = FooterComponent.layer.getText("value") as MultiStyleText;
        this.container = new PIXI.Container();

        canvasManager.onOrientationChange.add(this.onOrientationchange, this);

        this.setupFooterSymbols();
    }

    public showCustomResult(result: SymbolWinResult, textKey: string, args: any) {
        super.showCustomResult(result, textKey, args).execute();
        this.showSymbolsForResult(result);
        return Contract.empty();
    }

    public showWaysWin(result: WaysResult, symbolId: string) {
        super.showWaysWin(result, symbolId);
        this.showSymbolsForResult(result);
    }

    public showLineWin(result: LineResult, symbolId: string) {
        super.showLineWin(result, symbolId).execute();
        this.showSymbolsForResult(result);
        return Contract.empty();
    }

    protected setText(text: string) {
        super.setText(text);

        if (this.currentResult) {
            this.currentResult = null;
        }

        if (!this.footerText) {
            return;
        }

        this.hideSymbols();

        const originalPosition = this.getTextPosition();

        this.footerText.resize(originalPosition.width, originalPosition.height);
        this.footerText.move(originalPosition.x, originalPosition.y, "center");
    }

    private onOrientationchange(orientation: Orientation) {
        this.positionFooterSymbols();
        this.repositionText();
    }

    private getTextPosition() {
        const currentScene = FooterComponent.layer.getCurrentScene();

        if (canvasManager.orientation === Orientation.LANDSCAPE) {
            return currentScene.landscapeTextConfigs.get("value");
        } else {
            return currentScene.portraitTextConfigs.get("value");
        }
    }

    private hideSymbols() {
        this.container.children.forEach((child) => {
            child.visible = false;
        });
    }

    private showSymbolsForResult(result: SymbolWinResult) {
        if (SymbolFooter.SHOW_SINGLE_SYMBOL) {
            this.setText(`x${result.matches} ${this.footerText.text}`);
        }

        this.currentResult = result;

        for (let i = 0; i < this.numberOfReels; i++) {
            const symbol = this.symbols[i];

            if (i >= result.matches || (SymbolFooter.SHOW_SINGLE_SYMBOL && i > 0)) {
                symbol.visible = false;
                continue;
            }

            symbol.visible = true;
            symbol.texture = this.getSymbolTexture(result.symbols[i] || result.symbolType);
        }

        this.repositionText();
    }

    private repositionText() {
        if (!this.currentResult) {
            return;
        }

        const isPortrait = canvasManager.orientation === Orientation.PORTRAIT;
        const portraitExcess = isPortrait ? SymbolFooter.PORTRAIT_SCALE : 1;
        const originalTextPosition = this.getTextPosition();
        this.footerText.move(originalTextPosition.x, originalTextPosition.y, "left");

        const numSymbols = SymbolFooter.SHOW_SINGLE_SYMBOL ? 1 : this.currentResult.matches;
        const symbolsSpaceConsumed = numSymbols * this.symbolWidth * portraitExcess;
        const availableTextWidth = originalTextPosition.width - symbolsSpaceConsumed;
        this.footerText.resize(availableTextWidth, this.footerText.getHeight());
        const textWidth = this.footerText.width;
        const spaceNeeded = symbolsSpaceConsumed + textWidth;
        const unusedSpace = originalTextPosition.width - spaceNeeded;
        this.container.x = 10 + originalTextPosition.x + (unusedSpace / 2);
        this.footerText.x = this.container.x + symbolsSpaceConsumed - 5;
        this.container.x = 10 + originalTextPosition.x + (unusedSpace / 2);
        this.footerText.x = this.container.x + symbolsSpaceConsumed - 5;

        this.container.scale.set(portraitExcess, portraitExcess);
    }

    private setupFooterSymbols() {

        const symbolTexture = this.getSymbolTexture();
        const symbolScale = 1.3;

        for (let i = 0; i < this.numberOfReels; i++) {

            const symbol = new PIXI.Sprite(symbolTexture);

            symbol.anchor.set(0.5);

            if (SymbolFooter.SYMBOL_SIZE) {
                symbol.width = SymbolFooter.SYMBOL_SIZE;
                symbol.scale.y = symbol.scale.x;
            } else if (this.useLargeTextures) {
                symbol.scale.set(0.1);
            } else {
                symbol.scale.set(symbolScale);
            }

            symbol.visible = false;

            this.symbols.push(symbol);
            this.container.addChild(symbol);
        }

        const symbolSize = SymbolFooter.SYMBOL_SIZE || (symbolTexture.width * symbolScale);

        this.symbolWidth = symbolSize + symbolSize * SymbolFooter.SYMBOL_PADDING;
        this.positionFooterSymbols();
        FooterComponent.layer.add(this.container);
    }

    private positionFooterSymbols() {

        this.symbols.forEach((symbol, index) => {
            symbol.x = this.symbolWidth * index;
            symbol.y = 0;
        });

        const footerPostition = this.footerText.x - this.footerText.getWidth() / 2;
        this.container.x = 30 + footerPostition;
        this.container.y = this.footerText.y;
    }

    private symbolsHaveSmallTextures() {
        return SlotDefinition.symbolDefinitions.every(this.symbolHasSmallTexture);
    }

    private getSymbolTexture(symbolId?: string) {

        symbolId = symbolId ? symbolId : SlotDefinition.symbolDefinitions[0].id;

        if (this.useLargeTextures) {
            return GraphicsManager.getTexture(`${symbolId}`);
        } else {
            return GraphicsManager.getTexture(`${symbolId}_small`);
        }
    }

    private symbolHasSmallTexture(symbol: SymbolDefinition) {
        try {
            GraphicsManager.getTexture(`${symbol.id}_small`);
            return true;
        } catch {
            return false;
        }
    }
}
